import useSWR from 'swr';

import { getSegmentFromCookies } from '~/shared/utils/getSegmentFromCookies';
import { ICompany } from '~/modules/companies/interfaces/ICompany';
import { CompanyStatusEnum } from '~/modules/companies/enums/CompanyStatusEnum';
import { IAppConfigurationCompany } from '~/modules/apps/interfaces/IAppConfigurationCompany';
import { IAppConfiguration } from '~/modules/apps/interfaces/IAppConfiguration';
import { IApp } from '~/modules/apps/interfaces/IApp';

import { getCertificateAlert } from '../utils/getCertificateAlert';
import { IPraNotasCertificateAlert } from '../interfaces/IPraNotasCertificateAlert';

interface IUsePraNotasCertificatesAlerts {
  alerts: IPraNotasCertificateAlert[];
  highestSeverity: IPraNotasCertificateAlert['severity'];
  hasAlerts: boolean;
  hasExpired: boolean;
  isLoading: boolean;
}

const severityPriority: Record<IPraNotasCertificateAlert['severity'], number> =
  {
    none: 0,
    info: 1,
    warning: 2,
    critical: 3,
    expired: 4,
  };

function isActiveCompany(company?: ICompany): boolean {
  return company?.status?.system === CompanyStatusEnum.active;
}

export const usePraNotasCertificatesAlerts =
  (): IUsePraNotasCertificatesAlerts => {
    const segment = getSegmentFromCookies();
    const { data: app, error: appError } = useSWR<IApp>(
      `/apps/${segment}/pra-notas`
    );
    const { data: companies, error: companiesError } = useSWR<ICompany[]>(
      `/segments/${segment}/companies`
    );
    const activeCompaniesIds = new Set(
      (companies || [])
        .filter((company) => isActiveCompany(company))
        .map((company) => company._id)
    );

    const alerts = (app?.appConfigurations || [])
      .filter((appConfiguration: IAppConfiguration) =>
        activeCompaniesIds.has(
          (appConfiguration.company as IAppConfigurationCompany)?._id
        )
      )
      .map((appConfiguration: IAppConfiguration) => {
        const company = appConfiguration.company as IAppConfigurationCompany;

        return getCertificateAlert({
          companyId: company._id,
          certificateDueDate:
            appConfiguration.integration?.metadata?.certificateDueDate,
        });
      })
      .filter(
        (alert): alert is IPraNotasCertificateAlert =>
          !!alert && alert.severity !== 'none'
      )
      .sort(
        (left, right) => left.daysUntilExpiration - right.daysUntilExpiration
      );

    const highestSeverity = alerts.reduce<
      IPraNotasCertificateAlert['severity']
    >((current, alert) => {
      return severityPriority[alert.severity] > severityPriority[current]
        ? alert.severity
        : current;
    }, 'none');

    return {
      alerts,
      highestSeverity,
      hasAlerts: alerts.length > 0,
      hasExpired: alerts.some((alert) => alert.isExpired),
      isLoading: (!app && !appError) || (!companies && !companiesError),
    };
  };
